import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code,
  Calendar,
  Eye,
  X,
  ArrowRight,
  Share2,
  Heart,
  Bookmark,
  Clock,
  User,
  Tag,
  Search,
  Filter,
  MessageCircle,
  TrendingUp
} from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    title: 'Revolucionando APIs con IA en 2025',
    excerpt: 'Descubre cómo la inteligencia artificial está transformando las APIs en herramientas predictivas y ultra-seguras para empresas visionarias.',
    date: '2025-01-15',
    readTime: '5 min',
    image: '/images/sistema/gastro1.png',
    content: `
      <p>En <strong>Visioner7</strong>, estamos a la vanguardia de la integración IA-APIs. Imagina endpoints que anticipan tus necesidades antes de que las pidas. Usamos modelos de machine learning para optimizar llamadas, reducir latencia y prevenir brechas de seguridad en tiempo real.</p>
      
      <h3>Casos de Éxito Comprobados</h3>
      <p>Nuestro último proyecto con una fintech líder demostró un <strong>40% de mejora en eficiencia</strong> y un <strong>60% de reducción en tiempos de respuesta</strong>. Implementamos algoritmos predictivos que analizan patrones de uso para optimizar recursos automáticamente.</p>
      
      <h3>Tecnologías Implementadas</h3>
      <ul>
        <li>Machine Learning para optimización predictiva</li>
        <li>Blockchain para seguridad transaccional</li>
        <li>Microservicios escalables</li>
        <li>Monitoreo en tiempo real 24/7</li>
      </ul>
      
      <p>¿Listo para el futuro? Contáctanos y hagamos que tus datos trabajen de manera inteligente.</p>
    `,
    views: 1245,
    likes: 89,
    author: 'María González',
    authorRole: 'Líder de Desarrollo API',
    authorAvatar: '/images/logo22.png',
    category: 'APIs',
    tags: ['IA', 'APIs', 'Machine Learning', 'Seguridad'],
    featured: true
  },
  {
    id: 2,
    title: 'Desarrollo de Apps Móviles: De Concepto a Lanzamiento',
    excerpt: 'Paso a paso para crear apps nativas que dominen iOS y Android, con tips exclusivos de nuestros expertos en Visioner7.',
    date: '2025-01-10',
    readTime: '7 min',
    image: '/images/sistema/gastro1.png',
    content: `
      <p>En el mundo móvil actual, la velocidad de desarrollo y la calidad son cruciales. En <strong>Visioner7</strong>, hemos perfeccionado una metodología que combina agilidad con excelencia técnica.</p>
      
      <h3>Nuestro Enfoque Metodológico</h3>
      <p>Utilizamos <strong>React Native y Flutter</strong> para crear prototipos funcionales en horas, no semanas. Nuestro proceso incluye:</p>
      
      <ul>
        <li>Diseño centrado en el usuario desde día uno</li>
        <li>Desarrollo iterativo con feedback continuo</li>
        <li>Pruebas automatizadas exhaustivas</li>
        <li>Optimización de rendimiento nativo</li>
      </ul>
      
      <h3>Resultados Tangibles</h3>
      <p>Una app de contabilidad que desarrollamos procesa transacciones en vivo mientras rastrea gastos en tiempo real, logrando un <strong>99.8% de uptime</strong> y calificaciones de 4.9+ en stores.</p>
    `,
    views: 987,
    likes: 67,
    author: 'Carlos Rodríguez',
    authorRole: 'Especialista en Mobile',
    authorAvatar: '/images/logo22.png',
    category: 'Mobile',
    tags: ['React Native', 'Flutter', 'iOS', 'Android', 'UX'],
    featured: true
  },
  {
    id: 3,
    title: 'Sistemas Empresariales y Automatización Contable',
    excerpt: 'Cómo los sistemas inteligentes de Visioner7 automatizan la contabilidad y liberan a tu equipo para innovar, no para números aburridos.',
    date: '2025-01-05',
    readTime: '6 min',
    image: '/images/sistema/gastro1.png',
    content: `
      <p>La contabilidad moderna ya no debe ser un dolor de cabeza. Con nuestros <strong>sistemas ERP personalizados</strong>, integramos finanzas, inventarios y reportes en dashboards unificadas e intuitivas.</p>
      
      <h3>Innovaciones Clave</h3>
      <p>Implementamos <strong>blockchain para transacciones inmutables</strong> e <strong>IA para predicciones fiscales</strong> precisas. Un cliente del sector retail experimentó un <strong>30% de ahorro en costos de auditoría</strong> durante el primer año.</p>
      
      <h3>Características Destacadas</h3>
      <ul>
        <li>Automatización de procesos contables</li>
        <li>Reportes financieros en tiempo real</li>
        <li>Integración con sistemas bancarios</li>
        <li>Predictivos de flujo de caja</li>
      </ul>
    `,
    views: 1567,
    likes: 123,
    author: 'Ana Martínez',
    authorRole: 'Consultora ERP',
    authorAvatar: '/images/logo22.png',
    category: 'ERP',
    tags: ['Contabilidad', 'Automatización', 'ERP', 'Blockchain'],
    featured: false
  },
  {
    id: 4,
    title: 'Tendencias en Desarrollo Web para Empresas Tech',
    excerpt: 'Explora las novedades en web dev que Visioner7 implementa: PWAs, edge computing y más para sitios ultra-rápidos.',
    date: '2024-12-28',
    readTime: '4 min',
    image: '/images/sistema/gastro1.png',
    content: `
      <p>El desarrollo web evoluciona a velocidad exponencial. En <strong>Visioner7</strong>, estamos constantemente investigando e implementando las últimas tecnologías para mantener a nuestros clientes a la vanguardia.</p>
      
      <h3>Tecnologías que Marcaron 2024</h3>
      <p><strong>Progressive Web Apps (PWAs)</strong> que funcionan offline y cargan en milisegundos, combinadas con <strong>edge computing</strong> para reducir latencia global y <strong>arquitecturas serverless</strong> para escalabilidad infinita.</p>
      
      <h3>Stack Tecnológico Recomendado</h3>
      <ul>
        <li>Next.js para renderizado híbrido</li>
        <li>Vercel para despliegues automáticos</li>
        <li>GraphQL para APIs eficientes</li>
        <li>WebAssembly para aplicaciones pesadas</li>
      </ul>
    `,
    views: 2034,
    likes: 156,
    author: 'David Chen',
    authorRole: 'Arquitecto Frontend',
    authorAvatar: '/images/logo22.png',
    category: 'Web',
    tags: ['PWAs', 'Next.js', 'Performance', 'Edge Computing'],
    featured: true
  },
  {
    id: 5,
    title: 'Seguridad en APIs: Mejores Prácticas 2025',
    excerpt: 'Guía completa para proteger tus APIs contra amenazas modernas con las estrategias que implementamos en Visioner7.',
    date: '2024-12-20',
    readTime: '8 min',
    image: '/images/sistema/gastro1.png',
    content: 'Contenido detallado sobre seguridad en APIs...',
    views: 1789,
    likes: 98,
    author: 'Laura Fernández',
    authorRole: 'Especialista en Seguridad',
    authorAvatar: '/images/team/laura.jpg',
    category: 'Seguridad',
    tags: ['Seguridad', 'APIs', 'OWASP', 'JWT'],
    featured: false
  },
  {
    id: 6,
    title: 'Machine Learning en Aplicaciones Empresariales',
    excerpt: 'Cómo integrar IA y ML en tus sistemas existentes para obtener insights valiosos y automatizar procesos complejos.',
    date: '2024-12-15',
    readTime: '10 min',
    image: '/images/sistema/gastro1.png',
    content: 'Contenido sobre machine learning empresarial...',
    views: 1456,
    likes: 112,
    author: 'Roberto Silva',
    authorRole: 'Data Scientist',
    authorAvatar: '/images/logo22.png',
    category: 'IA',
    tags: ['Machine Learning', 'IA', 'Data Science', 'Python'],
    featured: true
  }
];

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [bookmarkedPosts, setBookmarkedPosts] = useState(new Set());

  // Categorías únicas
  const categories = ['todos', ...new Set(blogPosts.map(post => post.category))];

  // Filtrar posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'todos' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Posts destacados
  const featuredPosts = blogPosts.filter(post => post.featured);

  // Manejar like
  const handleLike = (postId, e) => {
    e.stopPropagation();
    setLikedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  // Manejar bookmark
  const handleBookmark = (postId, e) => {
    e.stopPropagation();
    setBookmarkedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  // Compartir post
  const handleShare = (post, e) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href + `#post-${post.id}`,
      });
    } else {
      navigator.clipboard.writeText(window.location.href + `#post-${post.id}`);
      // Mostrar notificación de copiado
    }
  };

  return (
    <section id="blog" className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Elementos decorativos */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center space-x-2 mb-4 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white mx-auto shadow-lg"
          >
            <Code className="w-5 h-5" />
            <span className="font-semibold">Blog Tecnológico Visioner7</span>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Insights & <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Tendencias</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Descubre las últimas innovaciones en desarrollo, IA y transformación digital.
            Nuestro equipo comparte conocimiento de vanguardia para impulsar tu negocio.
          </p>
        </motion.div>

        {/* Barra de búsqueda y filtros */}
      
{/*         <motion.div
          className="flex flex-col lg:flex-row gap-6 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar artículos..."
              className="w-full pl-12 pr-4 py-4 bg-white rounded-2xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 shadow-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            {categories.map(category => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 capitalize ${selectedCategory === category
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md border border-gray-200'
                  }`}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </motion.div>
 */}
        {/* Posts destacados */}
        {searchTerm === '' && selectedCategory === 'todos' && (
          <motion.div
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              Artículos Destacados
            </h3>

            <div className="grid lg:grid-cols-2 gap-8">
              {featuredPosts.slice(0, 2).map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 group cursor-pointer"
                  onClick={() => setSelectedPost(post)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Destacado
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">{post.title}</h3>
                    <p className="text-gray-600 mb-4">{post.excerpt}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">{post.author}</span>
                      </div>
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="text-blue-600 font-semibold flex items-center gap-1"
                      >
                        Leer más <ArrowRight className="w-4 h-4" />
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Grid de posts */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          layout
        >
          <AnimatePresence>
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                layout
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 group cursor-pointer"
                onClick={() => setSelectedPost(post)}
                whileHover={{ y: -8 }}
              >
                {/* Imagen */}
                <div className="relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                  />

                  {/* Overlay de hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-6">
                    <div className="text-white">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {post.category}
                      </span>
                    </div>
                    <motion.div
                      initial={{ scale: 0 }}
                      whileHover={{ scale: 1.1 }}
                      className="bg-white/20 backdrop-blur-sm rounded-full p-3"
                    >
                      <ArrowRight className="w-5 h-5 text-white" />
                    </motion.div>
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-6">
                  {/* Meta información */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">{post.views}</span>
                    </div>
                  </div>

                  {/* Título y excerpt */}
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-4">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => handleLike(post.id, e)}
                        className={`flex items-center gap-1 text-sm ${likedPosts.has(post.id) ? 'text-red-600' : 'text-gray-400 hover:text-red-500'
                          }`}
                      >
                        <Heart className={`w-4 h-4 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} />
                        <span>{post.likes + (likedPosts.has(post.id) ? 1 : 0)}</span>
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => handleBookmark(post.id, e)}
                        className={`${bookmarkedPosts.has(post.id) ? 'text-blue-600' : 'text-gray-400 hover:text-blue-500'
                          }`}
                      >
                        <Bookmark className={`w-4 h-4 ${bookmarkedPosts.has(post.id) ? 'fill-current' : ''}`} />
                      </motion.button>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Mensaje si no hay resultados */}
        {filteredPosts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No se encontraron artículos</h3>
            <p className="text-gray-600">Intenta con otros términos de búsqueda o categorías.</p>
          </motion.div>
        )}
      </div>

      {/* Modal de post completo */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-3xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header del modal */}
              <div className="sticky top-0 bg-white p-6 border-b rounded-t-3xl z-10">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {selectedPost.category}
                    </span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setSelectedPost(null)}
                    className="bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </motion.button>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-4">{selectedPost.title}</h2>

                <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{selectedPost.author}</span>
                    <span className="text-gray-400">•</span>
                    <span>{selectedPost.authorRole}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{selectedPost.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      <span>{selectedPost.views} vistas</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{selectedPost.readTime}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contenido del post */}
              <div className="p-6 space-y-6">
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-64 object-cover rounded-2xl shadow-lg"
                />

                <div
                  className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                />

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-6 border-t">
                  {selectedPost.tags.map(tag => (
                    <span key={tag} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Acciones del post */}
                <div className="flex items-center justify-between pt-6 border-t">
                  <div className="flex items-center gap-4">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => handleLike(selectedPost.id, e)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full ${likedPosts.has(selectedPost.id)
                          ? 'bg-red-50 text-red-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      <Heart className={`w-5 h-5 ${likedPosts.has(selectedPost.id) ? 'fill-current' : ''}`} />
                      <span>{selectedPost.likes + (likedPosts.has(selectedPost.id) ? 1 : 0)}</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => handleBookmark(selectedPost.id, e)}
                      className={`p-2 rounded-full ${bookmarkedPosts.has(selectedPost.id)
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      <Bookmark className={`w-5 h-5 ${bookmarkedPosts.has(selectedPost.id) ? 'fill-current' : ''}`} />
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => handleShare(selectedPost, e)}
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                      <Share2 className="w-5 h-5" />
                    </motion.button>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full font-semibold flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Comentar
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Blog;